<template>
  <div class="betConfirmationModal" :class="{ 'ru-locale': $i18n.locale === 'ru' }">
    <div class="header">
      <span class="header-title">{{ $t('coinPusherDropSelection') }}</span>
    </div>

    <div class="balance-wrapper">
      <UIBalance
        :token-address="blockchain.contracts[tokenToBet].addresses.contract"
        :label="$t('coreOwned')"
        is-format-balance
      />
    </div>

    <div class="betSizeContainer">
      <span class="greyTitle">{{ $t('coinPusherTicketPrice') }}:</span>
      <div class="iconValueContainer">
        <img :src="tokenImage" class="img-md" alt="" />
        <span class="whiteTitleMedium">{{ pusherTicketPriceInjected }}</span>
      </div>
    </div>

    <hr />

    <div class="pusherTicketsRemainingModal">
      <span class="greyTitle">{{ $t('coinPusherTicketsRemaining') }}:</span>
      <span class="orangeTitleBold">{{ data?.remainingBetLimit }}</span>
    </div>

    <UIInputNumberWrapper>
      <div
        class="buttons-container"
        :class="{
          'buttons-container-mobile': $device.isMobile
        }"
      >
        <button
          class="btn-outline btn-min"
          type="button"
          :disabled="pusherMaxBetsInjected === 0"
          @click="pusherClickMinInjected"
        >
          <span>{{ $t('coreMinimum') }}</span>
        </button>
        <el-input-number
          v-model="pusherBetCountInjected"
          :step="1"
          :step-strictly="true"
          :validate-event="false"
          :min="pusherMaxBetsInjected === 0 ? 0 : BET_COUNT_MIN"
          :max="pusherMaxBetsInjected"
          :value-on-clear="BET_COUNT_MIN"
          :disabled="pusherMaxBetsInjected === 0"
        />
        <button
          class="btn-outline btn-max"
          type="button"
          :disabled="pusherMaxBetsInjected === 0"
          @click="pusherClickMaxInjected"
        >
          <span>{{ $t('coreMax') }}</span>
        </button>
      </div>
    </UIInputNumberWrapper>

    <div class="betSizeContainerYourBet">
      <span class="greyTitle">{{ $t('coreYourBet') }}:</span>
      <div class="iconValueContainer">
        <img :src="tokenImage" class="img-md" alt="" />
        <span class="whiteTitleMedium">{{ pusherBetSizeInjected }}</span>
      </div>
    </div>

    <TavernFlipCoinPointsForBet
      v-if="pusherPointsForBetInjected"
      :points-for-bet="pusherPointsForBetInjected"
      :label-text="$t('tavernPointsForBet')"
    />

    <div class="buttonWrapper">
      <TavernFlipCoinBigButton
        :label="$t(pusherIsNotEnoughTokensInjected ? 'coreNotEnoughTokens' : 'coinPusherPlaceBet')"
        :disabled="isPlaceBetDisabled"
        @click="pusherStartGameInjected"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Tokens } from '~/utils/constants';
import useEnvs from '~/composables/useEnvs';
import tokens from 'public/img/tokens';
import { type PusherConfigResponse } from '~/types/Pusher';
import { BET_COUNT_MIN } from '~/utils/constants/pusher';
const { blockchain } = useEnvs();
const { data } = useNuxtData<PusherConfigResponse>('pusherConfig');
const { token } = defineProps<{ token: Tokens }>();
const tokenToBet = computed(() => token || Tokens.pearl);

const pusherBetCountInjected = inject<Ref<number>>('pusherBetCount');
const pusherBetSizeInjected = inject<Ref<number>>('pusherBetSize');
const pusherTicketPriceInjected = inject<Ref<number>>('pusherTicketPrice');
const pusherClickMaxInjected = inject<() => void>('pusherClickMax');
const pusherClickMinInjected = inject<() => void>('pusherClickMin');
const pusherStartGameInjected = inject<() => void>('pusherStartGame');
const pusherPointsForBetInjected = inject<Ref<number>>('pusherPointsForBet');
const pusherIsButtonClickedInjected = inject<Ref<boolean>>('pusherIsButtonClicked');
const pusherIsNotEnoughTokensInjected = inject<Ref<boolean>>('pusherIsNotEnoughTokens');
const pusherMaxBetsInjected = inject<Ref<number>>('pusherMaxBets');

const isPlaceBetDisabled = computed(
  () =>
    pusherIsButtonClickedInjected?.value || pusherIsNotEnoughTokensInjected?.value || pusherMaxBetsInjected?.value === 0
);

const tokenImage = computed(() => {
  return tokens[token || Tokens.pearl].toString();
});
</script>

<style scoped lang="scss">
.betConfirmationModal {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  hr {
    width: 300px;
    background-color: rgba(164, 150, 134, 1);
    height: 1px;
    border: none;
    margin: 17px auto;
  }

  .btn-outline {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .header {
    display: flex;
    justify-content: center;
    align-items: center;

    .header-title {
      font-weight: 700;
      font-family: Grenze;
      font-size: 32px;
      line-height: 1;
      color: var(--main-text-color);
    }
  }

  .buttons-container {
    display: flex;
    flex-direction: row;
    gap: 20px;
  }

  .buttons-container-mobile {
    gap: 9px;
  }

  .btn-max,
  .btn-min {
    font-family: Eczar;
    font-weight: 600;
    font-size: 18px;
    line-height: 1;
    border-radius: 12px;
    width: 54px;
    height: 45px;
  }

  .balance-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 43px;
    margin-top: 17px;
  }

  .betSizeContainer {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .betSizeContainerYourBet {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 17px;
  }

  .iconValueContainer {
    display: flex;
    align-items: center;
  }

  .pusherTicketsRemainingModal {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
  }

  .greyTitle {
    font-family: Eczar;
    font-weight: 500;
    font-size: 20px;
    line-height: 1.2;
    color: var(--dark-6);
  }

  .whiteTitleMedium {
    font-family: Eczar;
    font-weight: 500;
    font-size: 20px;
    line-height: 1.2;
    color: var(--main-text-color);
  }

  .orangeTitleBold {
    font-family: Eczar;
    font-weight: 700;
    font-size: 20px;
    line-height: 1;
    color: var(--orange);
  }

  .img-sm {
    height: 30px;
    width: 30px;
    max-width: 30px;
    max-height: 30px;
  }

  .pointsForBet {
    margin-top: 5px;
    width: fit-content;
  }

  .buttonWrapper {
    margin-top: 31px;
  }
}
</style>

<style lang="scss">
.betConfirmationModal {
  .img-md {
    height: 32px;
    width: 32px;
    max-height: 32px;
    max-width: 32px;
  }

  .gradient-button {
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: unset;
    width: 266px;
  }

  .balance-wrapper {
    .balance {
      padding: 0;
      background: transparent;
      border: none;
      box-shadow: none;
      backdrop-filter: none;

      .skeleton {
        width: 270px;
        max-width: unset;
      }

      .balance-label {
        font-family: Eczar;
        font-weight: 500;
        font-size: 24px;
        line-height: 1;
        color: var(--dark-7);
      }

      .price-value {
        font-family: Eczar;
        font-weight: 700;
        font-size: 24px;
        line-height: 1;
        color: var(--main-text-color);
      }

      .price-container {
        gap: 0;

        img {
          width: 40px;
          height: 40px;
        }
      }

      .price-value-zero {
        color: red;
      }
    }

    .el-skeleton {
      --el-skeleton-color: rgba(78, 49, 39, 0.1) !important;
      --el-skeleton-to-color: rgba(62, 43, 40, 0.7) !important;
    }
  }

  .input-number-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin-top: 17px;

    .el-input-number {
      min-width: 150px;

      @media screen and (max-width: 479px) {
        max-width: 130px;
      }

      &__decrease,
      &__increase {
        background: linear-gradient(to bottom, #f7ab06, #591500) !important;
      }

      .el-input__wrapper {
        background: transparent;
      }

      .el-input {
        background: transparent;
      }
    }
  }
}
</style>
